import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { OptionType, StepState } from 'src/app/core/enums/enums';
import { WaiverData, PassengerMco } from 'src/app/core/models/itinerary.model';
import { PaymentResponse } from 'src/app/core/models/PaymentResponse';
import {
  PaymentForm,
  PaymentMethod,
  ReissueRequestStatus
} from 'src/app/core/models/reissue.model';
import { ModuleSteps, Step } from 'src/app/core/models/step.model';
import { ExchangeQuoteRequest } from 'src/app/core/models/voluntary-change.model';
import { BookingActions, PaymentActions, ReissueActions } from '../actions';

export interface ReissueState {
  isLoading: boolean;
  steps: Step[];
  paymentMethods: PaymentMethod[];
  requestStatus: ReissueRequestStatus;
  isRetryAction: boolean;
  paymentForm: PaymentForm;
  paymentResult: PaymentResponse;
  quote: ExchangeQuoteRequest;
  isAutomatic: boolean;
  waiver: WaiverData;
  remarks: string[];
  callBook: boolean;
  passengersMco: PassengerMco[];
  remarksRequired: boolean;
}

export const initialState: ReissueState = {
  isLoading: false,
  steps: [],
  paymentMethods: [],
  requestStatus: null,
  isRetryAction: false,
  paymentForm: null,
  paymentResult: null,
  quote: null,
  isAutomatic: false,
  waiver: null,
  remarks: [],
  callBook: false,
  passengersMco: [],
  remarksRequired: true
};

const reissueReducer = createReducer(
  initialState,
  on(ReissueActions.LoadStepsSuccess, (state, { steps }) => ({
    ...state,
    steps,
    requestStatus: null
  })),
  on(PaymentActions.LoadPaymentMethods, (state, { paymentMethods }) => ({
    ...state,
    paymentMethods
  })),
  on(PaymentActions.Payment, (state) => ({
    ...state,
    isLoading: true,
    requestStatus: null,
    steps: new ModuleSteps(state.steps).stateOnActive(StepState.Active)
  })),
  on(PaymentActions.PaymentSuccess, (state, { paymentResult, isPaymentFirst }) => ({
    ...state,
    isLoading: false,
    requestStatus: null,
    paymentResult,
    steps: isPaymentFirst
      ? new ModuleSteps(new ModuleSteps(state.steps).stateOnActive(StepState.Completed)).active(1)
      : new ModuleSteps(new ModuleSteps(state.steps).stateOnActive(StepState.Completed)).active(2)
  })),
  on(PaymentActions.PaymentFail, (state, { requestStatus }) => ({
    ...state,
    isLoading: false,
    requestStatus,
    isRetryAction: true,
    steps: new ModuleSteps(state.steps).stateOnActive(StepState.Error)
  })),
  on(PaymentActions.ResetPaymentForm, (state) => ({
    ...state,
    isLoading: false,
    paymentMethods: [],
    paymentForm: null,
    paymentResult: null
  })),
  on(BookingActions.AutomaticBooking, (state) => ({
    ...state,
    isLoading: true,
    requestStatus: null,
    steps: new ModuleSteps(state.steps).stateOnActive(StepState.Active)
  })),
  on(BookingActions.AutomaticBookingSuccess, (state, { isPaymentFirst }) => ({
    ...state,
    isLoading: false,
    callBook: true,
    steps: isPaymentFirst
      ? new ModuleSteps(new ModuleSteps(state.steps).stateOnActive(StepState.Completed)).active(2)
      : new ModuleSteps(new ModuleSteps(state.steps).stateOnActive(StepState.Completed)).active(1)
  })),
  on(BookingActions.AutomaticBookingFail, (state, { requestStatus }) => ({
    ...state,
    isLoading: false,
    requestStatus,
    isRetryAction: true,
    steps: new ModuleSteps(state.steps).stateOnActive(StepState.Error)
  })),
  on(BookingActions.ManualBooking, (state) => ({
    ...state,
    isLoading: true,
    requestStatus: null,
    isRetryAction: false,
    steps: new ModuleSteps(state.steps).stateOnActive(StepState.Active)
  })),
  on(BookingActions.ManualBookingSuccess, (state, { isPaymentFirst }) => ({
    ...state,
    isLoading: false,
    callBook: true,
    steps: isPaymentFirst
      ? new ModuleSteps(new ModuleSteps(state.steps).stateOnActive(StepState.Completed)).active(2)
      : new ModuleSteps(new ModuleSteps(state.steps).stateOnActive(StepState.Completed)).active(1)
  })),
  on(BookingActions.ManualBookingFail, (state, { requestStatus }) => ({
    ...state,
    isLoading: false,
    requestStatus,
    isRetryAction: true,
    steps: new ModuleSteps(state.steps).stateOnActive(StepState.Error)
  })),
  on(ReissueActions.Reissue, (state, { formOfPayment, quote }) => ({
    ...state,
    isLoading: true,
    paymentForm: formOfPayment,
    quote,
    isRetryAction: false
  })),
  on(ReissueActions.ReissueSuccess, (state, { requestStatus, passengersMco }) => ({
    ...state,
    requestStatus,
    isLoading: false,
    steps: new ModuleSteps(state.steps).stateOnActive(StepState.Completed),
    passengersMco
  })),
  on(ReissueActions.ReissueFail, (state, { requestStatus, passengersMco }) => ({
    ...state,
    isLoading: false,
    requestStatus,
    isRetryAction: true,
    steps: new ModuleSteps(state.steps).stateOnActive(StepState.Error),
    passengersMco
  })),
  on(ReissueActions.SetBookingType, (state, { isAutomatic, waiver }) => ({
    ...state,
    isAutomatic,
    waiver
  })),
  on(ReissueActions.SaveRemarks, (state, { remarks }) => ({
    ...state,
    remarks
  })),
  on(ReissueActions.Finalize, () => ({
    ...initialState
  })),
  on(ReissueActions.ReissueClear, () => ({
    ...initialState
  })),
  on(ReissueActions.UpdatePassengersMco, (state, { passengerMco }) => ({
    ...state,
    passengersMco: state.passengersMco.map((passenger) => {
      if (passenger.id === passengerMco.id) {
        return { ...passenger, isOpen: !passenger.isOpen };
      }

      return passenger;
    })
  })),
  on(ReissueActions.CreateRemarks, (state) => ({
    ...state,
    isLoading: true
  })),
  on(ReissueActions.CreateRemarksSuccess, (state) => ({
    ...state,
    isLoading: false,
    remarksRequired: false
  }))
);

export function reducer(state: ReissueState | undefined, action: Action): ReissueState {
  return reissueReducer(state, action);
}

export const getReissueState = createFeatureSelector<ReissueState>(OptionType.Reissue);
export const getSteps = createSelector(getReissueState, (state) => state.steps);
export const getRequestStatus = createSelector(getReissueState, (state) => state.requestStatus);
export const getIsLoading = createSelector(getReissueState, (state) => state.isLoading);
export const getIsRetryAction = createSelector(getReissueState, (state) => state.isRetryAction);
export const getPaymentMethods = createSelector(getReissueState, (state) => state.paymentMethods);
export const getQuote = createSelector(getReissueState, (state) => state.quote);
export const getPaymentForm = createSelector(getReissueState, (state) => state.paymentForm);
export const getPaymentResult = createSelector(getReissueState, (state) => state.paymentResult);
export const getIsAutomatic = createSelector(getReissueState, (state) => state.isAutomatic);
export const getBookStatus = createSelector(getReissueState, (state) => state.callBook);
export const getWaiver = createSelector(getReissueState, (state) => state.waiver);
export const getRemarks = createSelector(getReissueState, (state) => state.remarks);
export const getPassengersWithMco = createSelector(getReissueState, (state) => state.passengersMco);
export const getRemarksRequired = createSelector(getReissueState, (state) => state.remarksRequired);
